import { useNavigate, useLocation, type NavigateFunction } from "react-router-dom";
import { useRoutes } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import routes from "./config";
import { useTheme } from "@/hooks/useTheme";
import BrandLogo from '@/components/feature/BrandLogo';
import { brandLogoTone } from '@/lib/placeholders';

let navigateResolver: (navigate: ReturnType<typeof useNavigate>) => void;

declare global {
  interface Window {
    REACT_APP_NAVIGATE: ReturnType<typeof useNavigate>;
  }
}

export const navigatePromise = new Promise<NavigateFunction>((resolve) => {
  navigateResolver = resolve;
});

export function AppRoutes() {
  const element = useRoutes(routes);
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [isNavigating, setIsNavigating] = useState(false);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    window.REACT_APP_NAVIGATE = navigate;
    navigateResolver(window.REACT_APP_NAVIGATE);
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  // Loading appears immediately on route change, stays until page renders + min time
  useEffect(() => {
    setIsNavigating(true);
    const start = performance.now();
    const MIN_DISPLAY = 350;

    const finish = () => {
      const elapsed = performance.now() - start;
      const remaining = Math.max(0, MIN_DISPLAY - elapsed);
      window.setTimeout(() => {
        // Wait for next paint to ensure page is actually rendered
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = requestAnimationFrame(() => {
            setIsNavigating(false);
          });
        });
      }, remaining);
    };

    finish();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [location.pathname]);

  return (
    <>
      <AnimatePresence>
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 12, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -12, scale: 0.98 }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          style={{ minHeight: "100dvh" }}
        >
          {element}
        </motion.div>
      </AnimatePresence>

      {/* Route transition loading — sits BETWEEN header/top-bar and bottom nav */}
      <AnimatePresence>
        {isNavigating && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className={`fixed top-[112px] bottom-20 left-0 right-0 z-30 flex flex-col items-center justify-center pointer-events-none ${
              isDark ? 'bg-dark/60' : 'bg-white/60'
            }`}
            style={{ backdropFilter: 'blur(2px)' }}
          >
            <motion.div
              className="flex flex-col items-center gap-4"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
            >
              <BrandLogo className={`w-14 h-14 ${brandLogoTone(isDark)}`} />
              <div className="flex items-center gap-2">
                <motion.div
                  className={`w-2 h-2 rounded-full ${isDark ? 'bg-gold' : 'bg-gold-dark'}`}
                  animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 0.8, repeat: Infinity, ease: "easeInOut", delay: 0 }}
                />
                <motion.div
                  className={`w-2 h-2 rounded-full ${isDark ? 'bg-gold' : 'bg-gold-dark'}`}
                  animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 0.8, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                />
                <motion.div
                  className={`w-2 h-2 rounded-full ${isDark ? 'bg-gold' : 'bg-gold-dark'}`}
                  animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 0.8, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}